import { type HydratedUserResult } from '@atlassian/jira-platform-fields/useHydratedUsers.tsx';

import {
	PARENT_FIELD_ID,
	USER_PICKER_FIELD_TYPES,
	COMPONENTS_FIELD_TYPE,
} from '../common/constants';
import type { DraftWorkItem } from '../common/types';
import type {
	DraftWorkItemHydratedUser,
	FieldConfigurationLike,
	IssueTypeToFieldsConfiguration,
	JiraCreateItemsAction,
} from '../controllers/use-ai-create-project-context/types';

import { recalculateHierarchy } from './recalculateHierarchy';

/**
 * Returns a map from a JiraComponentsField's label to its id.
 */
function getComponentsLabelToIdMap(
	fieldConfig: FieldConfigurationLike | undefined,
): Map<string, string> | null {
	if (!fieldConfig || fieldConfig.__typename !== COMPONENTS_FIELD_TYPE) {
		return null;
	}
	const edges = fieldConfig?.components?.edges;
	if (!edges) {
		return null;
	}
	const labelToId = new Map<string, string>();
	edges.forEach((edge) => {
		const name = edge?.node?.name;
		const id = edge?.node?.componentId;
		if (name && id) {
			labelToId.set(name, String(id));
		}
	});
	return labelToId.size > 0 ? labelToId : null;
}

/**
 * Converts incoming JiraComponentsField labels into IDs.
 */
function hydrateComponentsValue(value: unknown, labelToId: Map<string, string>): unknown {
	if (!Array.isArray(value)) {
		return value;
	}
	return value.map((v) => (typeof v === 'string' && labelToId.has(v) ? labelToId.get(v) : v));
}

function toDraftWorkItemHydratedUser(
	__typename: string,
	accountIdOrAri: string,
	hydratedUserResult?: HydratedUserResult,
): DraftWorkItemHydratedUser {
	if (!hydratedUserResult) {
		return {
			__typename,
			accountIdOrAri,
			status: 'INIT',
			error: null,
		};
	}
	return {
		__typename,
		accountIdOrAri: hydratedUserResult.accountIdOrAri,
		status: hydratedUserResult.status,
		error: hydratedUserResult.error,
		user: hydratedUserResult.user
			? {
					accountId: hydratedUserResult.user.accountId,
					name: hydratedUserResult.user.name,
					avatarUrl: hydratedUserResult.user.avatarUrl,
				}
			: undefined,
	};
}

/**
 * Returns a new fields object with `issuetype and summary` added if not already present.
 * @param fields - The original fields object.
 * @param extraFields - Extra fields to add to fields if missing.
 */
export function extendFieldsWithExtraFields({
	fields,
	extraFields,
}: {
	fields: Record<string, unknown>;
	extraFields: Record<string, unknown>;
}): {
	[x: string]: unknown;
} {
	const safeFields: Record<string, unknown> = {};
	const extraFieldKeys = Object.keys(extraFields);
	extraFieldKeys.forEach((key) => {
		// TODO - check in code review: why are we doing this? Why not just use the extraFields object? Is there a reason why the top level summary and fields would be different?
		if (!Object.prototype.hasOwnProperty.call(fields, key)) {
			safeFields[key] = extraFields[key];
		}
	});

	return { ...fields, ...safeFields };
}

/**
 * Converts an array of action objects into a map of `DraftWorkItem` objects,
 * establishing parent-child relationships based on the `fields.parent` property.
 *
 * - Flattens the input actions into `WorkItem` objects.
 * - Builds a lookup from summary to work item for efficient relationship mapping.
 * - Identifies child work items whose `fields.parent` matches the summary of a parent item.
 * - Returns a complete map of all work items (both parents and children) indexed by invocationId.
 * - childWorkItems contains arrays of invocationIds referencing child items.
 * - Validates field hierarchy by promoting any child work items that are the same hierarchy level as the parent.
 *
 * @param actions - Array of bulk action objects to be converted into draft work items.
 * @returns Map of invocationId to `DraftWorkItem` objects, with childWorkItems as invocationId arrays.
 */
export function toDraftWorkItems({
	actions,
	issueTypeMap,
	hydratedUsersMap,
}: {
	actions: JiraCreateItemsAction[];
	issueTypeMap?: IssueTypeToFieldsConfiguration;
	hydratedUsersMap?: Record<string, HydratedUserResult>;
}): {
	invocationIdToWorkItemMap: Record<string, DraftWorkItem>;
	topLevelInvocationIds: string[];
} {
	const invocationIdToWorkItemMap: Record<string, DraftWorkItem> = {};

	// Build the map directly from actions and prepare lookup maps
	actions.forEach((action) => {
		if (action.status === 'cancelled') {
			return;
		}

		const issueType = issueTypeMap?.[action.data.issueTypeId];

		let createdIssueKey: string | undefined;
		if (
			action.executedData &&
			typeof action.executedData === 'object' &&
			'createdIssueKey' in action.executedData &&
			typeof action.executedData.createdIssueKey === 'string'
		) {
			createdIssueKey = action.executedData.createdIssueKey;
		}

		const item = {
			invocationId: action.invocationId,
			projectIdOrKey: action.data.projectIdOrKey,
			issueTypeId: action.data.issueTypeId,
			summary: action.data.summary,
			status: action.status,
			createdIssueKey,
			fields: extendFieldsWithExtraFields({
				fields: {
					...Object.fromEntries(
						Object.entries(action?.data?.fields ?? {})
							.map(([fieldId, value]) => {
								const fieldConfig = issueType?.fieldsConfigurationNode?.[fieldId];

								// If there is no field config, that means the field was hallucinated by the LLM
								// So we'll skip those fields
								if (!fieldConfig) {
									// Sometimes AGG can miss the parent field, so we don't filter the parent field out even if there is no field config
									//
									if (fieldId === PARENT_FIELD_ID) {
										return [fieldId, value];
									}
									return [];
								}

								const fieldTypeName = fieldConfig.__typename;

								if (USER_PICKER_FIELD_TYPES.includes(fieldTypeName)) {
									if (Array.isArray(value)) {
										return [
											fieldId,
											value.map((v) =>
												toDraftWorkItemHydratedUser(fieldTypeName, v, hydratedUsersMap?.[v]),
											),
										];
									}
									if (typeof value !== 'string') {
										return [];
									}
									return [
										fieldId,
										toDraftWorkItemHydratedUser(fieldTypeName, value, hydratedUsersMap?.[value]),
									];
								}

								if (fieldTypeName === COMPONENTS_FIELD_TYPE) {
									const labelToId = getComponentsLabelToIdMap(fieldConfig);
									if (labelToId) {
										return [fieldId, hydrateComponentsValue(value, labelToId)];
									}
								}

								return [fieldId, value];
							})
							.filter((entry): entry is [string, DraftWorkItemHydratedUser] => !!entry?.length),
					),
				},
				extraFields: {
					// Add required fields from the issue type
					...Object.fromEntries(
						Object.values(issueType?.fieldsConfigurationNode ?? {})
							.filter((field) => field.fieldConfig?.isRequired)
							.map((field) => [field.fieldId, null]),
					),
					issuetype: issueType,
					summary: action.data.summary,
				},
			}),
		};

		invocationIdToWorkItemMap[action.invocationId] = item;
	});

	const { newMap, childInvocationIdsSet } = recalculateHierarchy({
		itemsMap: invocationIdToWorkItemMap,
		issueTypeMap,
	});

	// Calculate top-level items in order (those not in childInvocationIdsSet)
	const topLevelInvocationIds = actions
		.filter(
			(action) => !childInvocationIdsSet.has(action.invocationId) && action.status !== 'cancelled',
		)
		.map((action) => action.invocationId);

	return {
		invocationIdToWorkItemMap: newMap,
		topLevelInvocationIds,
	};
}
